import React, { useEffect, useState, useCallback } from "react";
import { adminAnalytics } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { RefreshCw, IndianRupee, ShoppingBag, TrendingUp, Compass } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid,
} from "recharts";

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="glass flex items-center gap-3 rounded-2xl p-4">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tint}`}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</div>
        <div className="font-display text-2xl text-white">{value}</div>
      </div>
    </div>
  );
}

const COLORS = ["#FF7A00", "#FFC107", "#FF9420", "#FFB300", "#FF6A00", "#F59E0B", "#FDBA74", "#EA580C"];

function TooltipContent({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-black/90 px-3 py-2 text-xs backdrop-blur">
      <div className="font-semibold text-white">{label}</div>
      <div className="text-[#FFC107]">{unit}{payload[0].value.toLocaleString("en-IN")}</div>
    </div>
  );
}

function ChartCard({ title, data, dataKey, unit, testid }) {
  const chartData = data.map((d) => ({ ...d, name: d.key }));
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-neutral-950 p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-xl text-white">{title}</h3>
        <span className="text-[11px] uppercase tracking-widest text-neutral-500">
          {data.length} {data.length === 1 ? "entry" : "entries"}
        </span>
      </div>
      {data.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-sm text-neutral-500">
          No attributed orders yet.
        </div>
      ) : (
        <div className="h-56 w-full">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <CartesianGrid stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="name" stroke="#a3a3a3" fontSize={11} tickLine={false} axisLine={false} interval={0} />
              <YAxis stroke="#a3a3a3" fontSize={11} tickLine={false} axisLine={false} width={40} />
              <Tooltip cursor={{ fill: "#ffffff08" }} content={<TooltipContent unit={unit} />} />
              <Bar dataKey={dataKey} radius={[8, 8, 0, 0]}>
                {chartData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function AnalyticsTab({ password, onUnauth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("revenue"); // revenue | orders

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAnalytics(password);
      setData(res);
    } catch (err) {
      if (err?.response?.status === 401) { onUnauth(); return; }
      toast.error("Failed to load analytics");
    } finally { setLoading(false); }
  }, [password, onUnauth]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || { orders: 0, revenue: 0, direct: 0 };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-white">Campaign Analytics</h2>
          <p className="text-sm text-neutral-500">Revenue and order attribution across Instagram / Facebook ads.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-white/10 bg-white/5 p-1 text-xs">
            {["revenue", "orders"].map((m) => (
              <button
                key={m}
                onClick={() => setMetric(m)}
                className={`rounded-full px-3 py-1 uppercase tracking-widest transition-colors ${
                  metric === m ? "bg-fire text-black" : "text-neutral-400 hover:text-white"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            className="rounded-full border border-white/10 bg-white/5 p-2 hover:bg-white/10"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div data-testid={TID.adminAnalyticsCards} className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon={ShoppingBag} label="Attributed Orders" value={t.orders} tint="bg-white/10 text-white" />
        <StatCard icon={IndianRupee} label="Attributed Revenue" value={`₹${t.revenue.toLocaleString("en-IN")}`} tint="bg-fire text-black" />
        <StatCard icon={Compass} label="Direct (No UTM)" value={t.direct} tint="bg-sky-500/20 text-sky-400" />
        <StatCard icon={TrendingUp} label="Avg Order Value" value={t.orders ? `₹${Math.round(t.revenue / t.orders)}` : "₹0"} tint="bg-amber-500/20 text-amber-300" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <ChartCard
          testid={TID.adminAnalyticsSource}
          title={`By Source — ${metric}`}
          data={data?.by_source || []}
          dataKey={metric}
          unit={metric === "revenue" ? "₹" : ""}
        />
        <ChartCard
          testid={TID.adminAnalyticsCampaign}
          title={`By Campaign — ${metric}`}
          data={data?.by_campaign || []}
          dataKey={metric}
          unit={metric === "revenue" ? "₹" : ""}
        />
        <ChartCard
          title={`By A/B Variant — ${metric}`}
          data={data?.by_variant || []}
          dataKey={metric}
          unit={metric === "revenue" ? "₹" : ""}
        />
      </div>

      <p className="mt-4 text-xs text-neutral-500">
        Tag your ads with{" "}
        <code className="rounded bg-white/5 px-1.5 py-0.5 text-neutral-300">?utm_source=instagram&amp;utm_campaign=launch&amp;v=muscle</code>{" "}
        to attribute orders automatically.
      </p>
    </>
  );
}
